import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import { toast } from "react-toastify";
import spApi from "store/services/sp.services";
import {
  HiOutlineAcademicCap,
  HiOutlineKey,
  HiOutlineShieldCheck,
} from "react-icons/hi";

const SETUP_STEPS = [
  {
    step: 1,
    title: "Open Wyzed integrations",
    detail: "Account Settings → Integrations → Install Wyzed API",
  },
  {
    step: 2,
    title: "Copy your credentials",
    detail: "Client ID, Client Secret, and API URL from the install screen",
  },
  {
    step: 3,
    title: "Paste below and connect",
    detail: "Vertex360 validates the connection and stores credentials securely",
  },
];

const Field = ({ label, hint, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700">{label}</label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    {error && <p className="mt-1.5 text-xs text-rose-600">{error}</p>}
  </div>
);

const inputClass =
  "mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400";

const WyzedConnectModal = ({ isOpen, closeModal, onConnected }) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      api_base_url: "",
      client_id: "",
      client_secret: "",
    },
  });

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await spApi.connectWyzed({
        api_base_url: data.api_base_url.trim(),
        client_id: data.client_id.trim(),
        client_secret: data.client_secret.trim(),
      });
      toast.success("Wyzed connected successfully.");
      onConnected?.();
      closeModal();
    } catch (err) {
      toast.error(err?.message ?? "Failed to connect Wyzed.");
    } finally {
      setIsLoading(false);
    }
  };

  const modalFooter = () => (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      <CustomButton
        label="Cancel"
        variant="secondary"
        isDisabled={isLoading}
        clickHandler={closeModal}
      />
      <CustomButton
        label="Connect Wyzed"
        showLoading={isLoading}
        isDisabled={isLoading}
        clickHandler={() => document.getElementById("wyzedConnectSubmit")?.click()}
      />
    </div>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      cssClass="max-w-2xl w-full"
      title="Connect Wyzed"
      closeModal={closeModal}
      modalFooter={modalFooter()}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm">
            <HiOutlineAcademicCap className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Link your organisation&apos;s Wyzed account
            </p>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              Once connected, you can map workers, sync training progress, and download
              certificates from worker profiles in Vertex360.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Setup guide
          </p>
          <ol className="mt-3 space-y-3">
            {SETUP_STEPS.map((item) => (
              <li key={item.step} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700">
                  {item.step}
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm font-medium text-slate-800">{item.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{item.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <HiOutlineKey className="h-5 w-5 text-slate-500" />
            <p className="text-sm font-semibold text-slate-800">API credentials</p>
          </div>

          <Field
            label="Client ID"
            hint="Numeric ID from Wyzed (digits only)"
            error={errors.client_id?.message}
          >
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 12345"
              className={inputClass}
              {...register("client_id", {
                required: "Client ID is required",
                pattern: {
                  value: /^\d+$/,
                  message:
                    "Client ID must be a number. Copy it exactly from Wyzed.",
                },
              })}
            />
          </Field>

          <Field label="Client Secret" error={errors.client_secret?.message}>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Paste from Wyzed"
              className={inputClass}
              {...register("client_secret", { required: "Client Secret is required" })}
            />
          </Field>

          <Field
            label="API URL"
            hint="Copy the full URL from Wyzed (e.g. https://12345.au.api.wyzed.com/api/v1). Tenant ID is detected automatically."
            error={errors.api_base_url?.message}
          >
            <input
              type="url"
              placeholder="https://12345.au.api.wyzed.com/api/v1"
              className={inputClass}
              {...register("api_base_url", { required: "API URL is required" })}
            />
          </Field>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2.5 text-xs text-emerald-900">
          <HiOutlineShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          <p>
            Credentials are stored for your organisation only and used to communicate with
            Wyzed on your behalf.
          </p>
        </div>

        <button type="submit" id="wyzedConnectSubmit" className="hidden" />
      </form>
    </ModalSkeleton>
  );
};

export default WyzedConnectModal;
